import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import TemplatePhonePreview from '../components/TemplatePhonePreview';
import MarketingTemplateFields from '../components/MarketingTemplateFields';
import VariableTextField from '../components/VariableTextField';
import { buildMarketingPayload, createMarketingForm } from '../utils/marketingTemplateForm';
import './Templates.css';

const EMPTY_TEMPLATE = {
  name: '', language: 'en_US', category: 'UTILITY', header: '', body: '', footer: '',
  body_examples: [], header_examples: [], buttons: [],
};

function variableCount(text) {
  const matches = [...String(text || '').matchAll(/{{\s*(\d+)\s*}}/g)];
  return matches.reduce((highest, match) => Math.max(highest, Number(match[1])), 0);
}

function resizeExamples(values, count) {
  return Array.from({ length: count }, (_, index) => values[index] || '');
}

export default function Templates() {
  const { user } = useAuth();
  const businessName = user?.wa_config?.business_name || user?.name || 'Your Business';
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  function load() {
    setLoading(true);
    api.get('/templates?status=all')
      .then(r => { setTemplates(r.data); setError(null); })
      .catch(err => setError(err.response?.data?.message || 'Failed to load templates'))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const filtered = templates.filter(t => {
    const haystack = `${t.name} ${t.language} ${t.category || ''} ${t.status || ''}`.toLowerCase();
    return !query || haystack.includes(query.toLowerCase());
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Message Templates</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={load}>↻ Refresh</button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New Meta template</button>
        </div>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
        Create WhatsApp templates here and track their Meta review status. Approved basic templates can be used in a
        <Link to="/broadcasts/new" style={{ color: 'var(--primary-light)' }}> new broadcast</Link>. Specialty marketing formats need additional send-time support.
      </p>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13, lineHeight: 1.8 }}>
          <div style={{ fontWeight: 600, color: 'var(--danger)', marginBottom: 6 }}>⚠️ Could not load templates</div>
          <div style={{ color: 'var(--text-muted)', marginBottom: 10 }}>{error}</div>
          <Link to="/settings" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
            🔍 Go to Settings → Connect WhatsApp
          </Link>
        </div>
      )}

      {!error && (
        <div className="form-group" style={{ maxWidth: 360 }}>
          <input className="input" placeholder="Search templates…" value={query}
            onChange={e => setQuery(e.target.value)} />
        </div>
      )}

      {loading
        ? <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading…</div>
        : !error && filtered.length === 0
          ? (
            <div className="empty-state">
              <div className="empty-state-icon">📝</div>
              <div className="empty-state-text">
                {templates.length === 0 ? 'No Meta templates yet' : 'No templates match your search'}
              </div>
              {templates.length === 0 && (
                <button className="btn btn-primary btn-sm" style={{ marginTop: 16 }} onClick={() => setShowCreate(true)}>
                  Create your first template
                </button>
              )}
            </div>
          )
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filtered.map(t => <TemplateCard key={t.id || `${t.name}-${t.language}`} t={t} onPreview={() => setPreviewTemplate(t)} />)}
            </div>
          )
      }

      {showCreate && (
        <CreateTemplateModal
          businessName={businessName}
          onClose={() => setShowCreate(false)}
          onCreated={template => {
            setTemplates(current => [template, ...current.filter(item => !(item.name === template.name && item.language === template.language))]);
            setShowCreate(false);
          }}
        />
      )}
      {previewTemplate && (
        <div className="modal-overlay" onMouseDown={event => event.target === event.currentTarget && setPreviewTemplate(null)}>
          <div className="modal template-library-preview" role="dialog" aria-modal="true" aria-label={`Preview ${previewTemplate.name}`}>
            <div className="template-preview-heading">
              <div><div className="modal-title" style={{ marginBottom: 4 }}>{previewTemplate.name}</div>
                <span className="badge badge-gray">{previewTemplate.language}</span> <span className="badge badge-gray">{previewTemplate.status}</span>
              </div>
              <button type="button" className="btn btn-ghost btn-sm" aria-label="Close preview" onClick={() => setPreviewTemplate(null)}>✕</button>
            </div>
            <TemplatePhonePreview template={previewTemplate} businessName={businessName} />
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTemplateModal({ businessName, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('guided');
  const [marketingForm, setMarketingForm] = useState(createMarketingForm());
  const [uploadsInProgress, setUploadsInProgress] = useState(0);
  const bodyVariableCount = variableCount(form.body);
  const headerVariableCount = variableCount(form.header);
  const guidedPreview = { components: [
    ...(form.header ? [{ type: 'HEADER', format: 'TEXT', text: form.header }] : []),
    { type: 'BODY', text: form.body },
    ...(form.footer ? [{ type: 'FOOTER', text: form.footer }] : []),
    ...(form.buttons.some(text => text.trim()) ? [{ type: 'BUTTONS', buttons: form.buttons.filter(text => text.trim()).map(text => ({ type: 'QUICK_REPLY', text })) }] : []),
  ] };
  const marketingResult = buildMarketingPayload(marketingForm);
  const previewTemplate = mode === 'marketing' ? marketingResult.payload : guidedPreview;

  function set(field, value) {
    setForm(current => {
      const next = { ...current, [field]: value };
      if (field === 'body') next.body_examples = resizeExamples(current.body_examples, variableCount(value));
      if (field === 'header') next.header_examples = resizeExamples(current.header_examples, variableCount(value));
      return next;
    });
  }

  function setExample(field, index, value) {
    setForm(current => ({
      ...current,
      [field]: current[field].map((item, itemIndex) => itemIndex === index ? value : item),
    }));
  }

  async function submit(event) {
    event.preventDefault();
    if (!navigator.onLine) return toast.error('Connect to the internet before submitting a template to Meta');
    if (mode === 'marketing' && marketingResult.errors.length) return toast.error(marketingResult.errors[0], { duration: 8000 });
    if (mode === 'guided' && (!form.name || !form.body.trim())) return toast.error('Add a template name and message body');
    if (mode === 'guided' && (form.body_examples.some(value => !value.trim()) || form.header_examples.some(value => !value.trim()))) {
      return toast.error('Add a sample value for every variable');
    }

    setSaving(true);
    try {
      const { data } = await api.post('/templates', mode === 'marketing' ? { meta_payload: marketingResult.payload } : form);
      toast.success('Template sent to Meta for review');
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Could not create template', { duration: 8000 });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={event => event.target === event.currentTarget && !saving && onClose()}>
      <form className="modal template-create-modal" onSubmit={submit}>
        <div className="modal-title">Create Meta template</div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6, margin: '-10px 0 18px' }}>
          Meta reviews every new template. It can be used after its status changes to Approved.
        </p>

        <div className="template-mode-switch" role="group" aria-label="Template editor mode">
          <button type="button" className={`btn ${mode === 'guided' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('guided')}>Basic text template</button>
          <button type="button" className={`btn ${mode === 'marketing' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('marketing')}>Marketing template forms</button>
        </div>

        <div className="template-editor-grid">
          <div className="template-editor-fields">
        {mode === 'marketing' ? <MarketingTemplateFields form={marketingForm} setForm={setMarketingForm}
          onUploadingChange={delta => setUploadsInProgress(current => Math.max(0, current + delta))} /> : <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div className="form-group">
            <label className="label">Template name</label>
            <input className="input" required maxLength={512} value={form.name}
              onChange={event => set('name', event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="order_update" />
            <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 5 }}>Lowercase letters, numbers and underscores</div>
          </div>
          <div className="form-group">
            <label className="label">Category</label>
            <select className="select" value={form.category} onChange={event => set('category', event.target.value)}>
              <option value="UTILITY">Utility</option>
              <option value="MARKETING">Marketing</option>
            </select>
          </div>
          <div className="form-group">
            <label className="label">Language</label>
            <input className="input" required value={form.language} onChange={event => set('language', event.target.value)} placeholder="en_US" />
          </div>
        </div>

        <VariableTextField label="Header (optional)" value={form.header} onChange={value => set('header', value)}
          maxLength={60} maxVariables={1} showCount placeholder="Your order is ready" />
        {headerVariableCount > 0 && (
          <VariableExamples label="Header variable sample" values={form.header_examples}
            onChange={(index, value) => setExample('header_examples', index, value)} />
        )}

        <VariableTextField label="Message body" value={form.body} onChange={value => set('body', value)}
          multiline rows={6} required maxLength={1024} showCount
          hint="Place the cursor where a personalised value should appear, then insert a variable."
          placeholder={'Hi {{1}}, your order {{2}} is ready for collection.'} />
        {bodyVariableCount > 0 && (
          <VariableExamples label="Body variable samples" values={form.body_examples}
            onChange={(index, value) => setExample('body_examples', index, value)} />
        )}

        <div className="form-group">
          <label className="label">Footer <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <input className="input" maxLength={60} value={form.footer} onChange={event => set('footer', event.target.value)}
            placeholder="Reply STOP to opt out" />
        </div>

        <div className="form-group">
          <label className="label">Quick replies <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional, up to 3)</span></label>
          {form.buttons.map((button, index) => (
            <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input className="input" maxLength={25} value={button} placeholder={`Reply ${index + 1}`}
                onChange={event => set('buttons', form.buttons.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} />
              <button type="button" className="btn btn-ghost" aria-label={`Remove reply ${index + 1}`}
                onClick={() => set('buttons', form.buttons.filter((_, itemIndex) => itemIndex !== index))}>×</button>
            </div>
          ))}
          {form.buttons.length < 3 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => set('buttons', [...form.buttons, ''])}>+ Add quick reply</button>
          )}
        </div>
        </>}
          </div>
          <aside className="template-editor-preview" aria-label="Live mobile preview">
            <div className="label">Live mobile preview</div>
            <TemplatePhonePreview template={previewTemplate} businessName={businessName}
              bodyValues={mode === 'guided' ? form.body_examples : undefined} headerValues={mode === 'guided' ? form.header_examples : undefined} />
          </aside>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || uploadsInProgress > 0}>{saving ? 'Submitting…' : 'Submit to Meta'}</button>
        </div>
      </form>
    </div>
  );
}

function VariableExamples({ label, values, onChange }) {
  return (
    <div className="form-group" style={{ padding: 12, background: 'var(--surface2)', borderRadius: 8 }}>
      <label className="label">{label}</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
        {values.map((value, index) => (
          <input key={index} className="input" required value={value} onChange={event => onChange(index, event.target.value)}
            placeholder={`Sample for {{${index + 1}}}`} />
        ))}
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 6 }}>Samples help Meta review the template and are not sent to customers.</div>
    </div>
  );
}

function TemplateCard({ t, onPreview }) {
  const body = t.components?.find(c => c.type === 'BODY')?.text;
  const header = t.components?.find(c => c.type === 'HEADER');
  const footer = t.components?.find(c => c.type === 'FOOTER')?.text;
  const buttons = t.components?.find(c => c.type === 'BUTTONS')?.buttons || [];

  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>📝</span>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{t.name}</span>
          <span className="badge badge-gray">{t.language}</span>
          {t.category && <span className="badge badge-gray">{t.category}</span>}
          <span className={`badge ${t.status === 'APPROVED' ? 'badge-green' : t.status === 'REJECTED' ? 'badge-red' : 'badge-yellow'}`}>
            {t.status}
          </span>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onPreview} aria-label={`Preview ${t.name}`}>
          Preview
        </button>
      </div>

      <div style={{ marginTop: 12, padding: 14, background: 'var(--surface2)', borderRadius: 10, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
        {header?.text && <div style={{ fontWeight: 600, marginBottom: 6 }}>{header.text}</div>}
        {header?.format && header.format !== 'TEXT' && (
          <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 6 }}>[{header.format} header]</div>
        )}
        <div>{body || <span style={{ color: 'var(--text-muted)' }}>No body text</span>}</div>
        {footer && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>{footer}</div>}
        {buttons.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {buttons.map((b, i) => (
              <span key={i} className="badge" style={{ background: 'rgba(37,211,102,0.12)', color: 'var(--primary-light)' }}>
                {b.text || b.type}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
